/**
 * Map raw RTK Query errors from qchess-api into a small enum the chess screens
 * can switch on, so components stay free of magic status codes and error-string
 * literals. Same shape as qduelsErrors/qriotsErrors.
 *
 * qchess-api 4xx errors carry `{ error: "<code>" }` (no `{ data }` wrapper).
 */

import type { QchessErrorBody } from '@/types/qchess';

export type QchessErrorKind =
  | 'not_linked' // caller hasn't linked a Lichess account yet
  | 'opponent_not_linked'
  | 'cannot_challenge_self'
  | 'registration_closed'
  | 'bracket_not_startable'
  | 'not_enough_players'
  | 'bracket_already_complete'
  | 'invalid'
  | 'not_found'
  | 'auth'
  | 'network'
  | 'server'
  | 'unknown';

function isQchessErrorBody(data: unknown): data is QchessErrorBody {
  return (
    typeof data === 'object' &&
    data !== null &&
    typeof (data as { error?: unknown }).error === 'string'
  );
}

export function classifyQchessError(err: unknown): QchessErrorKind {
  const e = err as { status?: number | string; data?: unknown };

  if (e?.status === 'FETCH_ERROR' || e?.status === 'TIMEOUT_ERROR') {
    return 'network';
  }

  // Prefer the server-provided `error` code when present.
  if (isQchessErrorBody(e?.data)) {
    const code = e.data.error;
    // "you must link first" — caller-side link errors map to not_linked.
    if (code === 'not_linked' || code === 'lichess_not_linked' || code === 'player_not_linked')
      return 'not_linked';
    if (code === 'opponent_not_linked') return 'opponent_not_linked';
    if (code === 'cannot_challenge_self') return 'cannot_challenge_self';
    if (code === 'registration_closed') return 'registration_closed';
    if (code === 'bracket_not_startable') return 'bracket_not_startable';
    if (code === 'not_enough_players') return 'not_enough_players';
    if (code === 'bracket_already_complete') return 'bracket_already_complete';
    if (code === 'bracket_not_found' || code === 'challenge_not_found') return 'not_found';
    if (code === 'invalid_body') return 'invalid';
    if (code === 'missing_bearer_token' || code === 'invalid_token') return 'auth';
  }

  if (e?.status === 401) return 'auth';
  if (e?.status === 404) return 'not_found';
  if (typeof e?.status === 'number' && e.status >= 500) return 'server';
  return 'unknown';
}

/** i18n key (chess namespace) for a classified qchess error. */
export function qchessErrorKey(kind: QchessErrorKind): string {
  return `error.${kind}`;
}
